import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import {
  Mic,
  MicOff,
  Video as VideoIcon,
  VideoOff,
  PhoneOff,
  Monitor,
  MessageSquare,
  Users,
  Hand,
  MoreVertical,
  Settings,
  Maximize,
  Minimize,
  Copy,
  Check,
  Send,
  Smile,
  Grid,
  Focus,
  Shield,
  Clock,
  X,
  Phone,
  UserPlus,
  Circle,
  Sparkles,
  Bot
} from 'lucide-react';
import { createBackgroundProcessor } from './backgroundEffect';

const VideoRoom = ({ room, onLeave, guestConfig }) => {
  const { user } = useAuth();
  const [janus, setJanus] = useState(null);
  const [videoRoomPlugin, setVideoRoomPlugin] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [screenStream, setScreenStream] = useState(null);
  const [remoteFeeds, setRemoteFeeds] = useState({});
  const [janusConfig, setJanusConfig] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [connectionError, setConnectionError] = useState(null);

  // Media state
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [handRaised, setHandRaised] = useState(false);

  // UI state
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [spotlightFeed, setSpotlightFeed] = useState(null);
  const [showControls, setShowControls] = useState(true);
  const [showCallModal, setShowCallModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  // Timer
  const [duration, setDuration] = useState(0);

  // Recording / virtual background / breakout state
  const [isRecording, setIsRecording] = useState(false);
  const [showBackgrounds, setShowBackgrounds] = useState(false);
  const [bgMode, setBgMode] = useState('none');
  const [breakouts, setBreakouts] = useState([]);
  const [inBreakout, setInBreakout] = useState(null);
  const bgProcRef = useRef(null);
  const bgModeRef = useRef('none');
  const rawStreamRef = useRef(null);
  const configRef = useRef(null);
  const janusRef = useRef(null);
  const inBreakoutRef = useRef(null);
  const breakoutOverrideRef = useRef(null);

  const localVideoRef = useRef(null);
  const screenVideoRef = useRef(null);
  const remoteVideosRef = useRef({});
  const pluginHandleRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const containerRef = useRef(null);
  const remoteFeedsRef = useRef({});
  const originalVideoTrackRef = useRef(null);
  const dataChannelRef = useRef(null);

  // Keep ref in sync with state
  useEffect(() => {
    remoteFeedsRef.current = remoteFeeds;
  }, [remoteFeeds]);

  // Auto-hide controls
  useEffect(() => {
    const handleMouseMove = () => {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        if (!showChat && !showParticipants) {
          setShowControls(false);
        }
      }, 3000);
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [showChat, showParticipants]);

  // Duration timer
  useEffect(() => {
    const interval = setInterval(() => {
      setDuration(d => d + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    initializeJanus();

    return () => {
      cleanup();
    };
  }, []);

  const formatDuration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Restore the last background choice; it is applied once the publisher is up.
  useEffect(() => {
    try {
      const saved = localStorage.getItem('visageBg');
      if (saved && saved !== 'none') { bgModeRef.current = saved; setBgMode(saved); }
    } catch (e) {}
    if (!guestConfig) {
      api.get(`/visage/rooms/${room.id}/breakouts`)
        .then(r => setBreakouts(r.data.breakouts || [])).catch(() => {});
    }
    return () => { if (bgProcRef.current) bgProcRef.current.stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While in a breakout, poll for the close signal (the main-room data channel
  // cannot reach us here) and return to main when breakouts end.
  useEffect(() => {
    if (!inBreakout) return;
    const iv = setInterval(async () => {
      try {
        if (guestConfig) {
          const r = await fetch('/api/visage/guest/breakout-status', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: guestConfig._token }),
          });
          const d = await r.json();
          if (!d.active) returnToMain();
        } else {
          const r = await api.get(`/visage/rooms/${room.id}/breakouts`);
          if (!r.data.breakouts?.length) returnToMain();
        }
      } catch (e) {}
    }, 10000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inBreakout]);

  const initializeJanus = async () => {
    try {
      // Get Janus configuration from backend, including password from lobby
      const mediaPrefs = window.visageMediaPrefs || { videoEnabled: true, audioEnabled: true };
      const joinBody = {};
      if (mediaPrefs.password) {
        joinBody.password = mediaPrefs.password;
      }

      let config;
      if (guestConfig) {
        config = guestConfig;
      } else {
        const response = await api.post(`/visage/rooms/${room.id}/join`, joinBody);
        config = response.data;
      }
      if (breakoutOverrideRef.current) {
        config = {
          ...config,
          janus_room_id: breakoutOverrideRef.current.janus_room_id,
          janus_pin: breakoutOverrideRef.current.janus_pin,
        };
      }
      configRef.current = config;
      setJanusConfig(config);
      setIsRecording(!!config.is_recording);

      // Wait for Janus to be available
      await waitForJanus();

      // Get user media (use saved prefs from lobby if available)
      let stream;
      if (rawStreamRef.current) {
        stream = rawStreamRef.current;
      } else if (mediaPrefs.stream) {
        stream = mediaPrefs.stream;
        setVideoEnabled(mediaPrefs.videoEnabled);
        setAudioEnabled(mediaPrefs.audioEnabled);
      } else {
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });
      }

      setLocalStream(stream);
      rawStreamRef.current = stream;

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      // Save original video track for screen share revert
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        originalVideoTrackRef.current = videoTrack;
      }

      // Build ICE servers config for Janus
      const iceServers = config.ice_servers || [];

      // Initialize Janus
      window.Janus.init({
        debug: false,
        callback: () => {
          const janusInstance = new window.Janus({
            server: config.janus_url,
            iceServers: iceServers.length > 0 ? iceServers : undefined,
            success: () => {
              console.log("Janus session created");
              setJanus(janusInstance);
              janusRef.current = janusInstance;
              attachToVideoRoom(janusInstance, config, stream);
              setIsInitializing(false);
            },
            error: (error) => {
              console.error("Janus error:", error);
              setConnectionError("Failed to connect to video server. Please try again.");
              setIsInitializing(false);
            },
            destroyed: () => {
              console.log("Janus session destroyed");
            }
          });
        }
      });

    } catch (error) {
      console.error("Error initializing:", error);
      if (error.response?.status === 401) {
        setConnectionError("Invalid meeting password");
      } else if (error.response?.status === 400) {
        setConnectionError(error.response?.data?.detail || "Unable to join meeting");
      } else {
        setConnectionError("Failed to access camera/microphone or join meeting");
      }
      setIsInitializing(false);
    }
  };

  const waitForJanus = () => {
    return new Promise((resolve, reject) => {
      if (window.Janus) {
        resolve();
        return;
      }

      let attempts = 0;
      const checkInterval = setInterval(() => {
        if (window.Janus) {
          clearInterval(checkInterval);
          resolve();
        } else if (attempts++ > 50) {
          clearInterval(checkInterval);
          reject(new Error('Janus library not loaded'));
        }
      }, 100);
    });
  };

  const attachToVideoRoom = (janusInstance, config, stream) => {
    janusInstance.attach({
      plugin: "janus.plugin.videoroom",
      success: (pluginHandle) => {
        console.log("VideoRoom plugin attached");
        setVideoRoomPlugin(pluginHandle);
        pluginHandleRef.current = pluginHandle;

        const register = {
          request: "join",
          room: config.janus_room_id,
          pin: config.janus_pin || "",
          ptype: "publisher",
          display: config.display_name
        };

        pluginHandle.send({ message: register });
      },
      error: (error) => {
        console.error("Error attaching plugin:", error);
        setConnectionError("Failed to join video room");
      },
      onmessage: (msg, jsep) => {
        const event = msg["videoroom"];

        if (event === "joined") {
          publishOwnFeed(pluginHandleRef.current, stream);

          if (msg["publishers"]) {
            msg["publishers"].forEach((pub) => {
              subscribeToFeed(janusInstance, config, pub.id, pub.display);
            });
          }
        } else if (event === "event") {
          if (msg["publishers"]) {
            msg["publishers"].forEach((pub) => {
              subscribeToFeed(janusInstance, config, pub.id, pub.display);
            });
          } else if (msg["leaving"]) {
            removeRemoteFeed(msg["leaving"]);
          }
        }

        if (jsep && pluginHandleRef.current) {
          pluginHandleRef.current.handleRemoteJsep({ jsep: jsep });
        }
      },
      ondataopen: () => {
        console.log("Data channel open");
        dataChannelRef.current = true;
        if (bgModeRef.current !== 'none') {
          applyBackground(bgModeRef.current);
        }
      },
      ondata: (data) => {
        handleDataChannelMessage(data);
      },
      onlocalstream: (stream) => {
        console.log("Local stream attached");
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
      }
    });
  };

  const handleDataChannelMessage = (data) => {
    try {
      const msg = JSON.parse(data);
      if (msg.type === 'chat') {
        setMessages(prev => [...prev, {
          id: Date.now(),
          user: msg.from,
          text: msg.text,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }]);
      } else if (msg.type === 'handRaise') {
        // Update remote participant hand raise state
        setRemoteFeeds(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(feedId => {
            if (updated[feedId].display === msg.from) {
              updated[feedId] = { ...updated[feedId], handRaised: msg.raised };
            }
          });
          return updated;
        });
      } else if (msg.type === 'recording') {
        setIsRecording(!!msg.on);
      } else if (msg.type === 'breakout') {
        if (msg.target && msg.target === configRef.current?.display_name) {
          if (msg.breakout_id) moveToBreakout(msg.breakout_id, msg.label);
          else returnToMain();
        }
      } else if (msg.type === 'breakouts_closed') {
        if (inBreakoutRef.current) returnToMain();
      }
    } catch (e) {
      // Ignore non-JSON messages
    }
  };

  const sendDataChannelMessage = (payload) => {
    if (pluginHandleRef.current && dataChannelRef.current) {
      pluginHandleRef.current.data({ text: JSON.stringify(payload) });
    }
  };

  const publishOwnFeed = (pluginHandle, stream) => {
    if (!pluginHandle) return;

    pluginHandle.createOffer({
      stream: stream,
      media: { audioRecv: false, videoRecv: false, audioSend: true, videoSend: true, data: true },
      success: (jsep) => {
        const publish = { request: "configure", audio: true, video: true };
        pluginHandle.send({ message: publish, jsep: jsep });
      },
      error: (error) => {
        console.error("WebRTC error:", error);
      }
    });
  };

  const subscribeToFeed = (janusInstance, config, feedId, displayName) => {
    if (remoteFeedsRef.current[feedId]) return;

    let subscriberHandle = null;

    janusInstance.attach({
      plugin: "janus.plugin.videoroom",
      success: (pluginHandle) => {
        subscriberHandle = pluginHandle;

        const subscribe = {
          request: "join",
          room: config.janus_room_id,
          pin: config.janus_pin || "",
          ptype: "subscriber",
          feed: feedId
        };

        pluginHandle.send({ message: subscribe });

        setRemoteFeeds(prev => ({
          ...prev,
          [feedId]: { plugin: pluginHandle, display: displayName, stream: null, handRaised: false }
        }));
      },
      onmessage: (msg, jsep) => {
        if (jsep && subscriberHandle) {
          subscriberHandle.createAnswer({
            jsep: jsep,
            media: { audioSend: false, videoSend: false },
            success: (jsep) => {
              const body = { request: "start" };
              subscriberHandle.send({ message: body, jsep: jsep });
            }
          });
        }
      },
      onremotestream: (stream) => {
        setRemoteFeeds(prev => ({
          ...prev,
          [feedId]: { ...prev[feedId], stream: stream }
        }));

        const videoElement = remoteVideosRef.current[feedId];
        if (videoElement && stream) {
          videoElement.srcObject = stream;
        }
      }
    });
  };

  const removeRemoteFeed = (feedId) => {
    const feed = remoteFeedsRef.current[feedId];
    if (feed?.plugin) {
      feed.plugin.detach();
    }
    setRemoteFeeds(prev => {
      const updated = { ...prev };
      delete updated[feedId];
      return updated;
    });
  };

  const videoSender = () =>
    pluginHandleRef.current?.webrtcStuff?.pc?.getSenders()?.find(
      (se) => se.track?.kind === 'video');

  const applyBackground = async (mode) => {
    bgModeRef.current = mode;
    setBgMode(mode);
    try { localStorage.setItem('visageBg', mode); } catch (e) {}
    const sender = videoSender();
    if (mode === 'none') {
      if (bgProcRef.current) { bgProcRef.current.stop(); bgProcRef.current = null; }
      if (sender && originalVideoTrackRef.current && !isScreenSharing) {
        try { await sender.replaceTrack(bgProcRef.current?.track || originalVideoTrackRef.current); } catch (e) {}
      }
      if (localVideoRef.current && rawStreamRef.current) {
        localVideoRef.current.srcObject = rawStreamRef.current;
      }
      return;
    }
    if (!bgProcRef.current) {
      try {
        bgProcRef.current = await createBackgroundProcessor(rawStreamRef.current);
      } catch (e) {
        console.error('background init failed', e);
        return;
      }
      if (sender && !isScreenSharing) {
        try { await sender.replaceTrack(bgProcRef.current.track); } catch (e) {}
      }
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = bgProcRef.current.stream;
      }
    }
    bgProcRef.current.setMode(mode);
  };

  const toggleAstraea = async (present) => {
    try {
      await api.post(`/visage/rooms/${room.id}/astraea/${present ? 'dismiss' : 'summon'}`);
    } catch (e) {
      console.error('astraea toggle failed', e);
    }
  };

  const toggleRecording = async () => {
    try {
      const next = !isRecording;
      await api.post(`/visage/rooms/${room.id}/recording/${next ? 'start' : 'stop'}`);
      setIsRecording(next);
      sendDataChannelMessage({ type: 'recording', on: next });
    } catch (e) {
      console.error('recording toggle failed', e);
    }
  };

  const rejoinJanus = () => {
    // Tear down the current Janus session and rejoin (breakout moves).
    if (bgProcRef.current) { bgProcRef.current.stop(); bgProcRef.current = null; }
    Object.keys(remoteFeedsRef.current || {}).forEach((fid) => {
      try { remoteFeedsRef.current[fid]?.plugin?.detach(); } catch (e) {}
    });
    setRemoteFeeds({});
    dataChannelRef.current = false;
    try { janusRef.current?.destroy(); } catch (e) {}
    janusRef.current = null;
    setIsInitializing(true);
    setTimeout(() => initializeJanus(), 400);
  };

  const moveToBreakout = async (breakoutId, label) => {
    try {
      let det;
      if (guestConfig) {
        const r = await fetch('/api/visage/guest/breakout-join', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: guestConfig._token, breakout_id: breakoutId }),
        });
        if (!r.ok) return;
        det = await r.json();
      } else {
        det = (await api.post(`/visage/rooms/${room.id}/breakout-join`,
          { breakout_id: breakoutId })).data;
      }
      breakoutOverrideRef.current = {
        janus_room_id: det.janus_room_id, janus_pin: det.janus_pin };
      inBreakoutRef.current = { breakout_id: breakoutId, label: label || 'Breakout' };
      setInBreakout(inBreakoutRef.current);
      rejoinJanus();
    } catch (e) {
      console.error('breakout join failed', e);
    }
  };

  const returnToMain = () => {
    breakoutOverrideRef.current = null;
    inBreakoutRef.current = null;
    setInBreakout(null);
    rejoinJanus();
  };

  const openBreakouts = async (count) => {
    try {
      const r = await api.post(`/visage/rooms/${room.id}/breakouts`, { count });
      setBreakouts(r.data.breakouts || []);
    } catch (e) {
      console.error('open breakouts failed', e);
    }
  };

  const closeAllBreakouts = async () => {
    try {
      await api.post(`/visage/rooms/${room.id}/breakouts/close`);
      sendDataChannelMessage({ type: 'breakouts_closed' });
      setBreakouts([]);
      if (inBreakoutRef.current) returnToMain();
    } catch (e) {}
  };

  const assignToBreakout = (displayName, value) => {
    if (!value) return;
    if (value === 'main') {
      sendDataChannelMessage({ type: 'breakout', target: displayName, breakout_id: null });
    } else {
      const b = breakouts.find((x) => String(x.breakout_id) === String(value));
      if (b) {
        sendDataChannelMessage({
          type: 'breakout', target: displayName,
          breakout_id: b.breakout_id, label: b.label });
      }
    }
  };

  const toggleAudio = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioEnabled;
        setAudioEnabled(!audioEnabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoEnabled;
        setVideoEnabled(!videoEnabled);
      }
    }
  };

  const toggleScreenShare = async () => {
    if (isScreenSharing) {
      // Stop screen sharing — revert to camera
      if (screenStream) {
        screenStream.getTracks().forEach(track => track.stop());
        setScreenStream(null);
      }
      // Replace track back to camera
      if (pluginHandleRef.current && originalVideoTrackRef.current) {
        try {
          const sender = pluginHandleRef.current.webrtcStuff?.pc?.getSenders()?.find(
            s => s.track?.kind === 'video'
          );
          if (sender) {
            await sender.replaceTrack(bgProcRef.current?.track || originalVideoTrackRef.current);
          }
        } catch (err) {
          console.error('Error reverting to camera:', err);
        }
      }
      setIsScreenSharing(false);
    } else {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: { cursor: "always" },
          audio: false
        });

        setScreenStream(stream);
        setIsScreenSharing(true);

        // Replace the video track on the existing publisher with the screen share track
        const screenTrack = stream.getVideoTracks()[0];
        if (pluginHandleRef.current && screenTrack) {
          try {
            const sender = pluginHandleRef.current.webrtcStuff?.pc?.getSenders()?.find(
              s => s.track?.kind === 'video'
            );
            if (sender) {
              await sender.replaceTrack(screenTrack);
            }
          } catch (err) {
            console.error('Error replacing track for screen share:', err);
          }
        }

        // Handle when user stops sharing via browser UI
        screenTrack.onended = async () => {
          setScreenStream(null);
          setIsScreenSharing(false);
          // Revert to camera
          if (pluginHandleRef.current && originalVideoTrackRef.current) {
            try {
              const sender = pluginHandleRef.current.webrtcStuff?.pc?.getSenders()?.find(
                s => s.track?.kind === 'video'
              );
              if (sender) {
                await sender.replaceTrack(bgProcRef.current?.track || originalVideoTrackRef.current);
              }
            } catch (err) {
              console.error('Error reverting to camera:', err);
            }
          }
        };
      } catch (err) {
        console.error('Screen share error:', err);
      }
    }
  };

  const toggleHandRaise = () => {
    const newState = !handRaised;
    setHandRaised(newState);
    sendDataChannelMessage({
      type: 'handRaise',
      from: user?.username || 'Unknown',
      raised: newState
    });
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const copyMeetingLink = () => {
    const link = `${window.location.origin}/visage?join=${room.id}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const sendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const msgText = newMessage.trim();

    // Add to local messages
    setMessages(prev => [...prev, {
      id: Date.now(),
      user: user?.username || 'You',
      text: msgText,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }]);

    // Send via data channel to other participants
    sendDataChannelMessage({
      type: 'chat',
      from: user?.username || 'You',
      text: msgText
    });

    setNewMessage('');
  };

  const leaveRoom = async () => {
    try {
      if (!guestConfig) {
        await api.post(`/visage/rooms/${room.id}/leave`);
      }
    } catch (error) {
      console.error("Error leaving room:", error);
    }
    cleanup();
    onLeave();
  };

  const cleanup = () => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
    }

    Object.values(remoteFeedsRef.current).forEach(feed => {
      if (feed.plugin) feed.plugin.detach();
    });

    if (videoRoomPlugin) videoRoomPlugin.detach();
    if (janus) janus.destroy();

    // Clear media prefs
    delete window.visageMediaPrefs;
  };

  const participantCount = Object.keys(remoteFeeds).length + 1;

  if (isInitializing) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
          <p className="text-xl text-white font-medium">Joining meeting...</p>
          <p className="text-virgo-teal-400 mt-2">{room.name}</p>
        </div>
      </div>
    );
  }

  if (connectionError) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center p-4">
        <div className="bg-virgo-navy-700 rounded-2xl p-8 max-w-md w-full text-center border border-virgo-navy-600">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <X className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Connection Failed</h2>
          <p className="text-virgo-teal-400 mb-6">{connectionError}</p>
          <button
            onClick={onLeave}
            className="px-6 py-3 bg-virgo-navy-600 text-white rounded-xl hover:bg-virgo-navy-500 transition"
          >
            Back to Meetings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex flex-col"
    >
      {/* Header */}
      <div className={`flex-shrink-0 px-6 py-4 flex items-center justify-between transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center">
              <VideoIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{room.name}</h2>
              <div className="flex items-center gap-3 text-sm text-virgo-teal-400">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {formatDuration(duration)}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {participantCount}
                </span>
                <span className="flex items-center gap-1">
                  <Shield className="w-4 h-4 text-green-400" />
                  Encrypted
                </span>
                {isRecording && (
                  <span className="flex items-center gap-1 text-red-400 font-medium">
                    <Circle className="w-3 h-3 fill-red-500 text-red-500 animate-pulse" />
                    Recording
                  </span>
                )}
                {inBreakout && (
                  <span className="text-amber-300 font-medium">{inBreakout.label}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={copyMeetingLink}
            className="flex items-center gap-2 px-4 py-2 bg-virgo-navy-700 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-600 transition"
          >
            {copiedLink ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            {copiedLink ? 'Copied!' : 'Invite'}
          </button>

          <button
            onClick={() => setViewMode(viewMode === 'grid' ? 'spotlight' : 'grid')}
            className="p-2 bg-virgo-navy-700 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-600 transition"
            title={viewMode === 'grid' ? 'Spotlight view' : 'Grid view'}
          >
            {viewMode === 'grid' ? <Focus className="w-5 h-5" /> : <Grid className="w-5 h-5" />}
          </button>

          <button
            onClick={toggleFullscreen}
            className="p-2 bg-virgo-navy-700 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-600 transition"
          >
            {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Video Grid */}
        <div className="flex-1 p-4 overflow-auto">
          <div className={`h-full grid gap-3 ${
            participantCount === 1 ? 'grid-cols-1' :
            participantCount === 2 ? 'grid-cols-2' :
            participantCount <= 4 ? 'grid-cols-2' :
            participantCount <= 9 ? 'grid-cols-3' :
            'grid-cols-4'
          } auto-rows-fr`}>
            {/* Screen Share (if active) */}
            {isScreenSharing && screenStream && (
              <div className="col-span-full row-span-2 relative bg-virgo-navy-800 rounded-2xl overflow-hidden border-2 border-cyan-500">
                <video
                  ref={(el) => {
                    screenVideoRef.current = el;
                    if (el) el.srcObject = screenStream;
                  }}
                  autoPlay
                  playsInline
                  className="w-full h-full object-contain"
                />
                <div className="absolute top-4 left-4 bg-cyan-500 px-3 py-1 rounded-lg text-white text-sm flex items-center gap-2">
                  <Monitor className="w-4 h-4" />
                  You're sharing your screen
                </div>
              </div>
            )}

            {/* Local Video */}
            <div className="relative bg-virgo-navy-800 rounded-2xl overflow-hidden min-h-[200px] border border-virgo-navy-600 group">
              <video
                ref={(el) => { localVideoRef.current = el; if (el && localStream && el.srcObject !== localStream) el.srcObject = localStream; }}
                autoPlay
                muted
                playsInline
                className={`w-full h-full object-cover ${!videoEnabled ? 'hidden' : ''}`}
              />

              {!videoEnabled && (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center text-white text-3xl font-semibold">
                    {user?.username?.charAt(0).toUpperCase() || 'U'}
                  </div>
                </div>
              )}

              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                <div className="bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg text-white text-sm flex items-center gap-2">
                  {!audioEnabled && <MicOff className="w-4 h-4 text-red-400" />}
                  You
                </div>
                {handRaised && (
                  <div className="bg-amber-500 px-2 py-1 rounded-lg">
                    <Hand className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            </div>

            {/* Remote Videos */}
            {Object.entries(remoteFeeds).map(([feedId, feed]) => (
              <div
                key={feedId}
                className="relative bg-virgo-navy-800 rounded-2xl overflow-hidden min-h-[200px] border border-virgo-navy-600 group cursor-pointer"
                onClick={() => setSpotlightFeed(spotlightFeed === feedId ? null : feedId)}
              >
                <video
                  ref={(el) => {
                    remoteVideosRef.current[feedId] = el;
                    if (el && feed.stream) {
                      el.srcObject = feed.stream;
                    }
                  }}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />

                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                  <div className="bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg text-white text-sm">
                    {feed.display}
                  </div>
                  {feed.handRaised && (
                    <div className="bg-amber-500 px-2 py-1 rounded-lg">
                      <Hand className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Chat Sidebar */}
        {showChat && (
          <div className="w-80 flex-shrink-0 bg-virgo-navy-800 border-l border-virgo-navy-600 flex flex-col">
            <div className="p-4 border-b border-virgo-navy-600 flex items-center justify-between">
              <h3 className="font-semibold text-white">Chat</h3>
              <button
                onClick={() => setShowChat(false)}
                className="text-virgo-teal-400 hover:text-white transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center text-virgo-teal-400 py-8">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No messages yet</p>
                </div>
              ) : (
                messages.map((msg) => (
                  <div key={msg.id} className="bg-virgo-navy-700 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-white">{msg.user}</span>
                      <span className="text-xs text-virgo-teal-400">{msg.time}</span>
                    </div>
                    <p className="text-virgo-teal-300 text-sm">{msg.text}</p>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={sendMessage} className="p-4 border-t border-virgo-navy-600">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 bg-virgo-navy-700 border border-virgo-navy-600 rounded-lg text-white placeholder-virgo-navy-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
                <button
                  type="submit"
                  className="p-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Participants Sidebar */}
        {showParticipants && (
          <div className="w-80 flex-shrink-0 bg-virgo-navy-800 border-l border-virgo-navy-600 flex flex-col">
            <div className="p-4 border-b border-virgo-navy-600 flex items-center justify-between">
              <h3 className="font-semibold text-white">Participants ({participantCount})</h3>
              <button
                onClick={() => setShowParticipants(false)}
                className="text-virgo-teal-400 hover:text-white transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 space-y-2">
              {/* You */}
              <div className="flex items-center gap-3 p-3 bg-virgo-navy-700 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center text-white font-semibold">
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">{user?.username} (You)</p>
                  <p className="text-xs text-virgo-teal-400">Host</p>
                </div>
                <div className="flex items-center gap-2">
                  {!audioEnabled && <MicOff className="w-4 h-4 text-red-400" />}
                  {!videoEnabled && <VideoOff className="w-4 h-4 text-red-400" />}
                  {handRaised && <Hand className="w-4 h-4 text-amber-400" />}
                </div>
              </div>

              {/* Breakout rooms (staff) */}
              {!guestConfig && janusConfig?.is_moderator && (
                <div className="p-3 bg-virgo-navy-700/60 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-white">Breakout rooms</p>
                    {breakouts.length > 0 && (
                      <button onClick={closeAllBreakouts} className="text-xs text-red-300 hover:text-red-200">
                        Close all
                      </button>
                    )}
                  </div>
                  {breakouts.length === 0 ? (
                    <div className="flex gap-2">
                      {[2, 3].map((n) => (
                        <button
                          key={n}
                          onClick={() => openBreakouts(n)}
                          className="px-2 py-1 text-xs bg-virgo-navy-600 text-virgo-teal-300 rounded hover:bg-virgo-navy-500 transition"
                        >
                          Open {n}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {breakouts.map((b) => (
                        <button
                          key={b.breakout_id}
                          onClick={() => moveToBreakout(b.breakout_id, b.label)}
                          className="w-full text-left px-2 py-1 text-xs bg-virgo-navy-600 text-virgo-teal-300 rounded hover:bg-virgo-navy-500 transition"
                        >
                          Join {b.label}
                        </button>
                      ))}
                      {inBreakout && (
                        <button
                          onClick={returnToMain}
                          className="w-full text-left px-2 py-1 text-xs bg-virgo-navy-600 text-amber-300 rounded hover:bg-virgo-navy-500 transition"
                        >
                          Return to main
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Remote Participants */}
              {Object.entries(remoteFeeds).map(([feedId, feed]) => (
                <div key={feedId} className="flex items-center gap-3 p-3 bg-virgo-navy-700 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-semibold">
                    {feed.display?.charAt(0).toUpperCase() || 'G'}
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{feed.display}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!guestConfig && janusConfig?.is_moderator && breakouts.length > 0 && (
                      <select
                        className="text-xs bg-virgo-navy-600 text-virgo-teal-300 rounded px-1 py-0.5"
                        defaultValue=""
                        onChange={(e) => { assignToBreakout(feed.display, e.target.value); e.target.value = ''; }}
                      >
                        <option value="">Send to…</option>
                        <option value="main">Main room</option>
                        {breakouts.map((b) => (
                          <option key={b.breakout_id} value={b.breakout_id}>{b.label}</option>
                        ))}
                      </select>
                    )}
                    {feed.handRaised && <Hand className="w-4 h-4 text-amber-400" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Background picker */}
      {showBackgrounds && (
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-virgo-navy-800 border border-virgo-navy-600 rounded-2xl p-4 z-20 shadow-2xl">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-white">Background</p>
            <button onClick={() => setShowBackgrounds(false)} className="text-virgo-teal-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-2">
            {[
              { id: 'none', label: 'None' },
              { id: 'blur', label: 'Blur' },
              { id: 'navy', label: 'Navy' },
              { id: 'slate', label: 'Slate' },
              { id: 'warm', label: 'Warm' },
            ].map((o) => (
              <button
                key={o.id}
                onClick={() => applyBackground(o.id)}
                className={`px-3 py-2 text-sm rounded-lg transition ${
                  bgMode === o.id
                    ? 'bg-cyan-600 text-white'
                    : 'bg-virgo-navy-700 text-virgo-teal-300 hover:bg-virgo-navy-600'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className={`flex-shrink-0 px-6 py-4 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
        <div className="flex items-center justify-center gap-3">
          {/* Audio */}
          <button
            onClick={toggleAudio}
            className={`p-4 rounded-2xl transition-all ${
              audioEnabled
                ? 'bg-virgo-navy-700 text-white hover:bg-virgo-navy-600'
                : 'bg-red-500 text-white hover:bg-red-600'
            }`}
            title={audioEnabled ? 'Mute' : 'Unmute'}
          >
            {audioEnabled ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
          </button>

          {/* Video */}
          <button
            onClick={toggleVideo}
            className={`p-4 rounded-2xl transition-all ${
              videoEnabled
                ? 'bg-virgo-navy-700 text-white hover:bg-virgo-navy-600'
                : 'bg-red-500 text-white hover:bg-red-600'
            }`}
            title={videoEnabled ? 'Stop video' : 'Start video'}
          >
            {videoEnabled ? <VideoIcon className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
          </button>

          {/* Screen Share */}
          <button
            onClick={toggleScreenShare}
            className={`p-4 rounded-2xl transition-all ${
              isScreenSharing
                ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                : 'bg-virgo-navy-700 text-white hover:bg-virgo-navy-600'
            }`}
            title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
          >
            <Monitor className="w-6 h-6" />
          </button>

          {/* Hand Raise */}
          <button
            onClick={() => setShowBackgrounds(!showBackgrounds)}
            className={`p-4 rounded-2xl transition-all ${
              bgMode !== 'none'
                ? 'bg-cyan-600 text-white'
                : 'bg-virgo-navy-700 text-white hover:bg-virgo-navy-600'
            }`}
            title="Virtual background"
          >
            <Sparkles className="w-6 h-6" />
          </button>

          {!guestConfig && janusConfig?.is_moderator && (
            <button
              onClick={toggleRecording}
              className={`p-4 rounded-2xl transition-all ${
                isRecording
                  ? 'bg-red-600 text-white'
                  : 'bg-virgo-navy-700 text-white hover:bg-virgo-navy-600'
              }`}
              title={isRecording ? 'Stop recording' : 'Start recording'}
            >
              <Circle className={`w-6 h-6 ${isRecording ? 'fill-white animate-pulse' : ''}`} />
            </button>
          )}

          {!guestConfig && janusConfig?.is_moderator && (() => {
            const astraeaPresent = Object.values(remoteFeeds).some(
              (f) => (f.display || '').startsWith('Astraea'));
            return (
              <button
                onClick={() => toggleAstraea(astraeaPresent)}
                className={`p-4 rounded-2xl transition-all ${
                  astraeaPresent
                    ? 'bg-indigo-600 text-white'
                    : 'bg-virgo-navy-700 text-white hover:bg-virgo-navy-600'
                }`}
                title={astraeaPresent ? 'Dismiss Astraea (notes will file to the case)' : 'Summon Astraea'}
              >
                <Bot className="w-6 h-6" />
              </button>
            );
          })()}

          <button
            onClick={toggleHandRaise}
            className={`p-4 rounded-2xl transition-all ${
              handRaised
                ? 'bg-amber-500 text-white hover:bg-amber-600'
                : 'bg-virgo-navy-700 text-white hover:bg-virgo-navy-600'
            }`}
            title={handRaised ? 'Lower hand' : 'Raise hand'}
          >
            <Hand className="w-6 h-6" />
          </button>

          {/* Chat */}
          <button
            onClick={() => { setShowChat(!showChat); setShowParticipants(false); }}
            className={`p-4 rounded-2xl transition-all ${
              showChat
                ? 'bg-cyan-500 text-white'
                : 'bg-virgo-navy-700 text-white hover:bg-virgo-navy-600'
            }`}
            title="Chat"
          >
            <MessageSquare className="w-6 h-6" />
          </button>

          {/* Participants */}
          <button
            onClick={() => { setShowParticipants(!showParticipants); setShowChat(false); }}
            className={`p-4 rounded-2xl transition-all ${
              showParticipants
                ? 'bg-cyan-500 text-white'
                : 'bg-virgo-navy-700 text-white hover:bg-virgo-navy-600'
            }`}
            title="Participants"
          >
            <Users className="w-6 h-6" />
          </button>

          {/* Call/Invite User */}
          <button
            onClick={() => { if (!guestConfig) setShowCallModal(true); }}
            style={guestConfig ? { display: 'none' } : undefined}
            className="p-4 rounded-2xl bg-virgo-navy-700 text-white hover:bg-virgo-navy-600 transition-all"
            title="Call a user into meeting"
          >
            <UserPlus className="w-6 h-6" />
          </button>

          {/* Leave */}
          <button
            onClick={leaveRoom}
            className="p-4 rounded-2xl bg-red-500 text-white hover:bg-red-600 transition-all ml-4"
            title="Leave meeting"
          >
            <PhoneOff className="w-6 h-6" />
          </button>
        </div>
      </div>
      {/* Call User Modal */}
      {showCallModal && (
        <CallUserModal
          roomId={room.id}
          onClose={() => setShowCallModal(false)}
        />
      )}
    </div>
  );
};

const CallUserModal = ({ roomId, onClose }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calling, setCalling] = useState(null);
  const [callSent, setCallSent] = useState(null);

  useEffect(() => {
    loadOnlineUsers();
  }, []);

  const loadOnlineUsers = async () => {
    try {
      const response = await api.get('/visage/online-users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error loading online users:', error);
    } finally {
      setLoading(false);
    }
  };

  const callUser = async (userId, username) => {
    setCalling(userId);
    try {
      await api.post(`/visage/rooms/${roomId}/call`, { user_id: userId });
      setCallSent(userId);
      setCalling(null);
      setTimeout(() => setCallSent(null), 3000);
    } catch (error) {
      console.error('Error calling user:', error);
      setCalling(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-virgo-navy-700 rounded-2xl p-6 max-w-sm w-full mx-4 border border-virgo-navy-600 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-cyan-400" />
            Call User Into Meeting
          </h3>
          <button onClick={onClose} className="text-virgo-teal-400 hover:text-white transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-10 h-10 text-virgo-teal-500 mx-auto mb-2" />
            <p className="text-virgo-teal-400">No other users online</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-auto">
            {users.map(u => (
              <div
                key={u.id}
                className="flex items-center gap-3 p-3 bg-virgo-navy-800 rounded-xl hover:bg-virgo-navy-600 transition"
              >
                <div className="relative flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center text-white font-semibold">
                    {(u.full_name || u.username)?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-virgo-navy-800 ${u.online ? 'bg-green-500' : 'bg-virgo-navy-500'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{u.full_name || u.username}</p>
                  <p className={`text-xs ${u.online ? 'text-green-400' : 'text-virgo-teal-500'}`}>{u.online ? 'Online' : 'Offline'}</p>
                </div>
                <button
                  onClick={() => callUser(u.id, u.username)}
                  disabled={calling === u.id || callSent === u.id}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition flex-shrink-0 ${
                    callSent === u.id
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                      : calling === u.id
                      ? 'bg-virgo-navy-600 text-virgo-teal-400'
                      : 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30'
                  }`}
                >
                  {callSent === u.id ? 'Ringing!' : calling === u.id ? '...' : 'Call'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoRoom;
